import React, { useCallback, useMemo, useState } from "react";
import Footer from "components/commons/footer/Footer";
import CardWrapper from "components/commons/cardWrapper/CardWrapper";
import { useAppSelector } from "utils/store";
import { useRequest } from "../../../../../hooks/useRequest";
import {
  createCampaignThunk,
  submitAdOrderThunk,
} from "../../../../../core/orderAd/orderAd.thunk";
import styles from "./OrderPreview.module.scss";
import footerStyles from "../../../orderAd/orderDetails/OrderDetails.module.scss";
import classNames from "classnames";
import {
  AdSpot,
  ApiAdRecord,
} from "../../../../../core/orderAd/orderAd.entity";
import { AccountType } from "../../../../../core/auth/auth.entity";
import { strings } from "../../../../../intl/fa";
import {
  ArrowLeft,
  InfoSquare,
  NewButton,
  NewButtonColor,
  NewButtonSize,
} from "@bale/ui";
import { useMyPanel } from "../../../../../hooks/useMyPanel";
import { useConfigs } from "../../../../../hooks/useConfigs";
import { useTheme } from "@emotion/react";
import { AdType } from "../../../../../core/orderAd/orderAd.slice";
import PriceChangeModal from "components/commons/modal/priceChangeModal/PriceChangeModal";
import { digitsToHindi, toMoneyFormat } from "@bale/utils";
import { useCampaignPrice } from "hooks/useCampaignPrice";
import * as Sentry from "@sentry/react";
import { useCalculatedPrice } from "hooks/useCalculatedPrice";
import PriceFooter from "components/content/priceFooter/PriceFooter";

type RowProps = {
  rowKey?: string;
  value?: string;
  ltr?: boolean;
};
const Row: React.FC<React.PropsWithChildren<RowProps>> = (props) => {
  return (
    <div className={styles.Row}>
      <p className={styles.Key}>{props.rowKey}</p>
      <p
        className={styles.Value}
        style={{ direction: props.ltr ? "ltr" : "rtl" }}
      >
        {props.value}
      </p>
    </div>
  );
};
type Props = {
  isCampaign?: boolean;
};
const OrderPreview: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const [showPriceReductionModal, setShowPriceReductionModal] = useState(false);
  const orderLoading = useAppSelector((state) => state.orderAd.orderLoading);

  const { sendRequest: submitAdOrder } = useRequest(submitAdOrderThunk);
  const { sendRequest: createCampaign } = useRequest(createCampaignThunk);

  const submit = () => {
    if (!loading) {
      if (props.isCampaign) {
        createCampaign();
      } else {
        submitAdOrder();
      }
    }
  };

  const closePriceReductionModal = useCallback(() => {
    setShowPriceReductionModal(false);
  }, []);

  const orderAdInfo = useAppSelector((state) => state.orderAd.info);

  const linkIsForceJoin = useAppSelector(
    (state) => state.orderAd.linkIsForceJoin
  );
  const theme = useTheme();

  const { view_price, new_view_price, click_price, new_click_price } =
    useConfigs();

  const isView = (orderAdInfo?.view_count || 0) > 0;

  const { finalBasePrice, totalPrice, loading, finalPrice } =
    useCalculatedPrice({
      viewCount: orderAdInfo.view_count || 0,
      clickCount: orderAdInfo.click_count || 0,
      spot: orderAdInfo.spot || AdSpot.AdSpotDialog,
      link: orderAdInfo.link || "",
    });

  const adBasePrice = useMemo<number>(() => {
    if (orderAdInfo.view_count) {
      return totalPrice / orderAdInfo.view_count;
    } else if (orderAdInfo.click_count) {
      return totalPrice / orderAdInfo.click_count;
    }
    return 1;
  }, [orderAdInfo, totalPrice]);

  const isDiscountActive = isView
    ? view_price !== new_view_price
    : click_price !== new_click_price;

  return (
    <>
      <CardWrapper className={classNames(styles.Card, styles.PreviewCard)}>
        <p className={styles.Title}>{strings.preview.preview}</p>
        <div className={styles.RowsWrapper}>
          <div className={styles.Preview}>
            <img className={styles.Image} src={orderAdInfo.imageBlob} alt="" />
            <div className={styles.PreviewInfo}>
              <p className={styles.AdTitle}>{orderAdInfo.title}</p>
              <div className={styles.DescriptionWrapper}>
                <p className={styles.AdDescription}>
                  {orderAdInfo.description}
                </p>
                {linkIsForceJoin && (
                  <div className={styles.LinkWrapper}>
                    <p>{strings.preview.join}</p>
                    <ArrowLeft size={16} color={theme.colors.primary[40]} />
                  </div>
                )}
              </div>
            </div>
          </div>
          <Row
            rowKey={strings.order_details.ad_link}
            value={orderAdInfo.link}
            ltr
          />
          {props.isCampaign && (
            <Row
              rowKey={strings.campaign_details.campaign_type}
              value={
                orderAdInfo.adType === AdType.click
                  ? strings.clicky
                  : strings.viewy
              }
            />
          )}
          {orderAdInfo.view_count ? (
            <Row
              rowKey={strings.order_details.view_count}
              value={digitsToHindi(
                toMoneyFormat(String(orderAdInfo.view_count))
              )}
            />
          ) : null}
          {orderAdInfo.click_count ? (
            <Row
              rowKey={strings.order_details.click_count}
              value={digitsToHindi(
                toMoneyFormat(String(orderAdInfo.click_count))
              )}
            />
          ) : null}
        </div>
      </CardWrapper>
      {!props.isCampaign && isDiscountActive && (
        <div
          className={styles.InfoCard}
          onClick={() => setShowPriceReductionModal(true)}
        >
          <InfoSquare size={20} color={theme.colors.dark[300]} />
          <span className={styles.Description}>
            {strings.order_details.price_reduction_reason_button}
          </span>
          <ArrowLeft size={20} color={theme.colors.dark[300]} />
        </div>
      )}
      <PriceFooter
        button={{
          text: strings.final_submit,
          onClick: submit,
          loading: orderLoading,
        }}
        clickCount={orderAdInfo.click_count}
        viewCount={props.isCampaign ? 1 : orderAdInfo.view_count}
        showPrice
        isCampaign={props.isCampaign}
      />
      {showPriceReductionModal && (
        <PriceChangeModal
          labelText={
            isView
              ? strings.order_details.view_price
              : strings.order_details.click_price
          }
          primaryText={strings.common.button.understood}
          onPrimaryClicked={closePriceReductionModal}
          onClose={closePriceReductionModal}
          changeInfo={{
            afterPrice: adBasePrice,
            beforePrice: finalBasePrice,
          }}
          title={strings.order_details.price_reduction_modal.title}
          description={strings.order_details.price_reduction_modal.desc}
        />
      )}
    </>
  );
};

export default OrderPreview;
